import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { CampoControlado, Entrada, EntradaSensivel, Rotulo, Selecao, type Opcao } from '../../componentes/formulario';
import { dinheiro } from '../../nucleo/formato';
import type { ItemModelo } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';
import { anunciarOcultos } from '../../componentes/sensivel';

/** Linha em edição: a quantidade fica em texto para aceitar "1,5" enquanto se digita. */
export interface ItemForm {
  chave: string;
  descricao: string;
  quantidade: string;
  valor: number | null;
  categoria_id: string;
}

export const novoItem = (): ItemForm => ({ chave: crypto.randomUUID(), descricao: '', quantidade: '1', valor: null, categoria_id: '' });

export function itensDoModelo(itens: ItemModelo[]): ItemForm[] {
  return itens.map((i) => ({ chave: crypto.randomUUID(), descricao: i.descricao, quantidade: String(i.quantidade).replace('.', ','), valor: i.valor_unitario_centavos, categoria_id: i.categoria_id ?? '' }));
}

const quantidadeDe = (q: string) => {
  const n = Number(q.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

/** Total da linha para mostrar na tela; o servidor é quem arredonda de verdade (para o par). */
export const totalDaLinha = (i: ItemForm) => Math.round(quantidadeDe(i.quantidade) * (i.valor ?? 0));
export const totalDosItens = (itens: ItemForm[]) => itens.reduce((a, i) => a + totalDaLinha(i), 0);

/** Itens no formato da API. Lança Error com a mensagem pronta quando uma linha está incompleta. */
export function itensParaApi(itens: ItemForm[], msg: { semItens: string; incompleto: string }) {
  const validos = itens.filter((i) => i.descricao.trim() || i.valor);
  if (!validos.length) throw new Error(msg.semItens);
  return validos.map((i) => {
    const q = quantidadeDe(i.quantidade);
    if (!i.descricao.trim() || !i.valor || i.valor <= 0 || q <= 0) throw new Error(msg.incompleto);
    return { descricao: i.descricao.trim(), quantidade: q, valor_unitario_centavos: i.valor, categoria_id: i.categoria_id || null };
  });
}

/** Editor de linhas do documento: descrição, quantidade, valor unitário e categoria do Financeiro. */
export function ItensEditor({ itens, aoMudar, categorias, moeda }: { itens: ItemForm[]; aoMudar: (v: ItemForm[]) => void; categorias: Opcao[]; moeda: string }) {
  const { t } = useTranslation('contas');
  const muda = (chave: string, parcial: Partial<ItemForm>) => aoMudar(itens.map((i) => (i.chave === chave ? { ...i, ...parcial } : i)));
  return (
    <fieldset className="flex min-w-0 flex-col gap-3 sm:col-span-6">
      <legend className="mb-1 text-sm font-medium text-tinta-2">{t('itens.titulo')}</legend>
      {itens.map((i, n) => (
        <div key={i.chave} className="grid min-w-0 grid-cols-6 gap-2 rounded-lg border border-borda bg-sup-1 p-3">
          <div className="col-span-6">
            <Rotulo id={`item-desc-${i.chave}`} rotulo={`${t('itens.descricao')} ${n + 1}`}>
              <EntradaSensivel mascara={null} livre id={`item-desc-${i.chave}`} maxLength={300} value={i.descricao} onChange={(e) => muda(i.chave, { descricao: e.target.value })} />
            </Rotulo>
          </div>
          <div className="col-span-2">
            <Rotulo id={`item-qtd-${i.chave}`} rotulo={t('itens.quantidade')}>
              <Entrada id={`item-qtd-${i.chave}`} inputMode="decimal" className="numero text-right" value={i.quantidade} onChange={(e) => muda(i.chave, { quantidade: e.target.value })} />
            </Rotulo>
          </div>
          <div className="col-span-4 sm:col-span-2">
            <CampoControlado def={{ nome: `valor-${i.chave}`, rotulo: t('itens.valorUnitario'), tipo: 'dinheiro' }} valor={i.valor} aoMudar={(v) => muda(i.chave, { valor: v as number | null })} />
          </div>
          <div className="col-span-5 flex min-w-0 flex-col justify-end gap-1 sm:col-span-1">
            <span className="text-sm font-medium text-tinta-2">{t('itens.total')}</span>
            <TextoCortado className="numero flex h-10 items-center justify-end text-sm text-tinta-1">{anunciarOcultos(dinheiro(totalDaLinha(i), moeda))}</TextoCortado>
          </div>
          <div className="col-span-1 flex items-end justify-end">
            <Botao variante="fantasma" aria-label={t('itens.remover')} title={t('itens.remover')} disabled={itens.length === 1} onClick={() => aoMudar(itens.filter((x) => x.chave !== i.chave))} icone={<Trash2 className="size-4" />} />
          </div>
          <div className="col-span-6">
            <Rotulo id={`item-cat-${i.chave}`} rotulo={t('itens.categoria')}>
              <Selecao id={`item-cat-${i.chave}`} vazio={t('itens.semCategoria')} opcoes={categorias} value={i.categoria_id} onChange={(e) => muda(i.chave, { categoria_id: e.target.value })} />
            </Rotulo>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Botao icone={<Plus className="size-4" />} onClick={() => aoMudar([...itens, novoItem()])}>
          {t('itens.adicionar')}
        </Botao>
        <p className="numero text-sm text-tinta-2">
          {t('itens.soma')}: <span className="font-semibold text-tinta-1">{anunciarOcultos(dinheiro(totalDosItens(itens), moeda))}</span>
        </p>
      </div>
    </fieldset>
  );
}
